import React from 'react';
import { Heading } from 'apps/sigma2-ui/src/components/ui/Heading';
import { IconCircularArrowWithDotSvg } from 'apps/sigma2-ui/src/components/svg/IconCircularArrowWithDot';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { SimpleSlider } from 'apps/sigma2-ui/src/components/ui/SimpleSlider';
import { CustomVizOptionsSummaryDropdown } from 'libs/end-to-end/src/custom-viz/CustomVizDefinitions';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { InputBox } from 'apps/sigma2-ui/src/components/ui/InputBox';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { usePayBenchmarking } from 'apps/sigma2-ui/src/pay-benchmarking/hooks/usePayBenchmarking';

export const PayBenchmarkingFilterView = () => {
  const {
    selectedRole,
    selectedLevel,
    selectedCurrency,
    selectedLocation,
    selectedBenchmark,
    selectedDepartment,
    selectedExperience,
    selectedEducationLabel,
    selectedCompensationUnit,
    selectableRoles,
    selectableLevels,
    selectableLocations,
    selectableBenchmarks,
    selectableCurrencies,
    selectableDepartments,
    selectableEducationLabels,
    selectableCompensationUnits,
    canApplyFilters,
    setSelectedRole,
    setSelectedLevel,
    setSelectedCurrency,
    setSelectedLocation,
    setSelectedBenchmark,
    setSelectedDepartment,
    setSelectedExperience,
    setSelectedEducationLabel,
    setSelectedCompensationUnit,
    onApplyFilters,
  } = usePayBenchmarking();

  const language = useLanguage();

  const onResetFilters = () => {
    setSelectedBenchmark(null);
    setSelectedDepartment(null);
    setSelectedRole(null);
    setSelectedLevel(null);
    setSelectedLocation(null);
    setSelectedExperience(null);
    setSelectedEducationLabel(null);
    setSelectedCurrency(null);
    setSelectedCompensationUnit('salary');
  };

  const convertSelectableItems = (items: CustomVizOptionsSummaryDropdown[]) => {
    return items.map((item) => ({
      optionLabel: item.dropdownItemText,
      optionValue: item.dropdownItemValue,
    }));
  };

  return (
    <>
      <div className="h-full flex flex-col overflow-scroll w-[18rem] p-2">
        <div className="flex justify-between items-center gap-2 pb-2">
          <Heading level={4} textColor="text-black">
            Filters
          </Heading>
          <button onClick={onResetFilters}>
            <IconCircularArrowWithDotSvg
              svg={{ className: 'w-6 h-6 text-secondary' }}
            />
          </button>
        </div>

        <SelectBoxWithSearch
          type="single"
          list={convertSelectableItems(selectableBenchmarks)}
          label="Benchmark"
          setSelected={setSelectedBenchmark}
          selected={selectedBenchmark ?? ''}
          widthClass={'w-full'}
        />

        <div className="pt-4">
          <Paragraph textColor="text-black" className="font-bold text-sm">
            Position
          </Paragraph>

          <Spacing direction={'vertical'} size={'sm'}>
            <SelectBoxWithSearch
              type="single"
              list={convertSelectableItems(selectableDepartments)}
              label="Department"
              setSelected={setSelectedDepartment}
              selected={selectedDepartment ?? ''}
              widthClass={'w-full'}
            />

            <SelectBoxWithSearch
              type="single"
              list={convertSelectableItems(selectableRoles)}
              label="Role"
              setSelected={setSelectedRole}
              selected={selectedRole ?? ''}
              widthClass={'w-full'}
            />

            <SelectBoxWithSearch
              type="single"
              list={convertSelectableItems(selectableLevels)}
              label="Level"
              setSelected={setSelectedLevel}
              selected={selectedLevel ?? ''}
              widthClass={'w-full'}
            />

            <SelectBoxWithSearch
              type="single"
              list={convertSelectableItems(selectableLocations)}
              label="Location"
              setSelected={setSelectedLocation}
              selected={selectedLocation ?? ''}
              widthClass={'w-full'}
            />
          </Spacing>
        </div>

        <div className="pt-4">
          <Paragraph
            textColor="text-black"
            fontFamily="font-sans"
            className="font-bold text-sm"
          >
            Candidate
          </Paragraph>

          <Spacing direction={'vertical'} size={'sm'}>
            <InputBox
              label="Experience (in years)"
              value={selectedExperience ?? ''}
              setValue={setSelectedExperience}
              widthClass={'w-full'}
            />

            <SelectBoxWithSearch
              type="single"
              list={convertSelectableItems(selectableEducationLabels)}
              label="Education"
              setSelected={setSelectedEducationLabel}
              selected={selectedEducationLabel ?? ''}
              widthClass={'w-full'}
            />
          </Spacing>
        </div>

        <div className="pt-4">
          <Paragraph textColor="text-black" className="font-bold text-sm">
            Compensation
          </Paragraph>

          <Spacing direction={'vertical'} size={'sm'}>
            <div className="pt-2">
              <SimpleSlider
                currentValue={selectedCompensationUnit}
                selectableValues={selectableCompensationUnits}
                getRenderValue={(value) =>
                  value === 'salary'
                    ? language.compUnitSalary()
                    : language.compUnitHourly()
                }
                onChange={setSelectedCompensationUnit}
              />
            </div>

            <SelectBoxWithSearch
              type="single"
              list={convertSelectableItems(selectableCurrencies)}
              label="Currency"
              setSelected={setSelectedCurrency}
              selected={selectedCurrency ?? ''}
              widthClass={'w-full'}
            />
          </Spacing>
        </div>

        <div className="flex-grow self-stretch" />

        <div className="py-4">
          <LegacyButton
            data-qa="calculate-btn"
            disabled={!canApplyFilters}
            variant={LegacyButtonVariant.APPLY_BUTTON}
            onClick={onApplyFilters}
            className="font-sans relative cursor-default w-full text-center justify-center"
          >
            Apply
          </LegacyButton>
        </div>
      </div>
    </>
  );
};
